import { Component, Inject, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalService } from '@assetaccountant/core/services/modal.service';
import { HasTitle } from '@assetaccountant/core/services/title.service';
import { TrackingService } from '@assetaccountant/core/services/tracking.service';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';
import { emptyValue, TypedFormBuilder } from '@assetaccountant/core/utils/typed-forms';
import { OidcStateParams } from '@assetaccountant/integrations/models/oidc-state-params';
import { XeroTenant } from '@assetaccountant/integrations/models/xero/xero-tenant';
import { XeroService } from '@assetaccountant/integrations/services/xero.service';

@Component({
  selector: 'app-xero-select-tenant',
  templateUrl: './xero-select-tenant.component.html',
  styleUrls: ['./xero-select-tenant.component.scss'],
  standalone: false,
})
export class XeroSelectTenantComponent implements OnInit, HasTitle {
  tenants: XeroTenant[];
  isLoaded = false;
  isConnecting = false;
  isNewConnection = false;
  isReconnect = false;
  returnRoute: URL;
  registerId: number;
  xeroUserId: string;

  form = this.fb.group({
    tenantId: [{ value: '', disabled: !this.isLoaded }, Validators.required],
    organisationId: [emptyValue<number>()],
    registerId: [emptyValue<number>()],
  });

  get currentYear() {
    return new Date().getFullYear();
  }

  get title() {
    return { pageTitle: 'Select Xero Tenant' };
  }

  constructor(
    private fb: TypedFormBuilder,
    private router: Router,
    @Inject(ActivatedRoute)
    private route: TypedActivatedRoute<{}, {}, { code: string; state: string; error: string }>,
    private xeroService: XeroService,
    private modalService: ModalService,
    private trackingService: TrackingService,
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const localState = localStorage.getItem('aa-oidc-state');
      if (params && localState === params.state) {
        const stateParams: OidcStateParams = JSON.parse(atob(params.state));
        if (params.error) {
          this.router.navigate([stateParams.returnRoute]);
          throw params.error;
        }

        const tenantMatch = /\btenantId=([^#]*)/.exec(stateParams.returnRoute);
        this.returnRoute = new URL(stateParams.returnRoute, location.origin);
        if (this.returnRoute.searchParams.has('tenantId')) {
          this.returnRoute.searchParams.delete('tenantId');
          this.isReconnect = true;
        }

        const redirectMatch = /\bregisters\/(\d+)/.exec(stateParams.returnRoute);
        const registerId = redirectMatch && redirectMatch[1];

        if (registerId == null) {
          this.isNewConnection = true;
        } else {
          this.registerId = +registerId;
        }

        this.xeroService.listTenants(params.code).subscribe({
          next: listTenantsResponse => {
            this.form.controls.tenantId.enable();
            this.tenants = listTenantsResponse.tenants;

            if (this.tenants.length === 0) {
              this.modalService
                .showErrorModal(
                  'No available tenants found, please contact support.',
                  'No Tenants Found',
                )
                .subscribe(() => this.onCancelClick());
            } else if (this.isReconnect && tenantMatch && tenantMatch[1]) {
              const previouslyConnectedTenant = this.tenants.find(
                t => t.tenantId === tenantMatch[1],
              );

              if (previouslyConnectedTenant) {
                this.form.controls.tenantId.setValue(tenantMatch[1].toString());
                this.isLoaded = true;
                return;
              }

              this.modalService
                .showQuestionModal(
                  "<p>We've noticed that you are refreshing a previously established connection but we couldn't find your previously connected tenant from Xero.</p> <p>Please ensure you are using the correct Xero account to reconnect.</p>",
                  'Previous Tenant Not Found',
                  'Cancel Reconnection',
                  'Continue Reconnecting',
                )
                .subscribe(() => this.onCancelClick());
            } else if (this.tenants.length === 1) {
              this.form.controls.tenantId.setValue(this.tenants[0].tenantId.toString());
            }
            this.isLoaded = true;
          },
          error: error => {
            location.href = stateParams.returnRoute;
            throw error;
          },
        });
      } else {
        this.router.navigate(['/organisations/-']);
      }
    });
  }

  onConnectClick() {
    this.isConnecting = true;
    this.form.disable();

    if (!this.form.value.organisationId) {
      this.trackingService.initaliseUserTracking(true);
      this.trackingService.startSessionReplay();
      this.trackingService.trackEvent('Create new organisation (app store)', 'Xero');
    }

    const command = {
      connectionId:
        this.tenants.find(tenant => tenant.tenantId === this.form.value.tenantId)?.id || '',
      tenantId: this.form.value.tenantId,
      tenantName:
        this.tenants.find(tenant => tenant.tenantId === this.form.value.tenantId)?.tenantName || '',
      registerId: this.registerId || +this.form.value.registerId || null,
      organisationId: +this.form.value.organisationId || null,
    };

    this.xeroService.connect(command).subscribe({
      next: response => {
        this.isConnecting = false;
        if (this.isNewConnection) {
          this.router.navigateByUrl(
            `/organisations/${response.integration.organisationId}/registers/${response.integration.registerId}/start`,
          );
        } else {
          location.href = this.returnRoute.toString();
        }
        return;
      },
      error: error => {
        this.isConnecting = false;
        this.form.enable();
        throw error;
      },
    });
    return;
  }

  onCancelClick() {
    if (this.isNewConnection) {
      location.href = this.returnRoute.toString();
      return;
    }

    this.router.navigateByUrl(
      `/organisations/-/registers/${this.registerId}/settings#integrations`,
    );
    return;
  }
}
